import {
  Button,
  Carousel,
  Heading,
  Icon,
  Modal,
  Text,
  useToastContext,
} from "@ttc3k/trekker";
import {
  AssetImage,
  OperatorWithOverrides,
  WidgetByIdQuery,
} from "gql/generated";
import { EditPencil, Undo, Xmark } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { getMultilingualString } from "shared/utils";
import { Flex, Grid } from "styled-system/jsx";
import { useWidgetOperatorOverridesModal } from "./useWidgetOperatorOverridesModal";
import { useWidgetOperatorOverridesForm } from "./useWidgetOperatorOverridesForm";
import { FormProvider, useWatch } from "react-hook-form";
import {
  CloudinaryImage,
  ControlledTextArea,
  OperatorWidgetCard,
} from "shared/components";
import { limitFill } from "@cloudinary/transformation-builder-sdk/actions/resize";
import { isLandscape } from "shared/utils/isLandscape";
import { autoGravity } from "@cloudinary/transformation-builder-sdk/qualifiers/gravity";
import _ from "lodash";
import { useParams } from "react-router-dom";

type WidgetOperatorOverridesModalProps = {
  isOpen: boolean;
  operator: OperatorWithOverrides | undefined;
  widget: WidgetByIdQuery["widgetById"];
  onClose: () => void;
};

export const WidgetOperatorOverridesModal = ({
  isOpen,
  operator,
  widget,
  onClose,
}: WidgetOperatorOverridesModalProps) => {
  const { t } = useTranslation();
  const { toastFactory } = useToastContext();
  const { organizationId } = useParams();

  const formId = "widget-operator-overrides-form";
  const { form, onSubmit } = useWidgetOperatorOverridesForm({
    operator,
    widget,
    onSuccess: () => {
      form.reset();
      onClose();
    },
    onError: () => {
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("widgets:DETAILS.FORM.ERROR", {
          widgetName: getMultilingualString(widget?.name ?? {}),
        }),
      });
    },
  });

  // We need to watch the shortDescription AND images to update the preview card correctly
  const images = useWatch({ control: form.control, name: "images" });
  const shortDescription = useWatch({
    control: form.control,
    name: "shortDescription",
  });
  const description = useWatch({ control: form.control, name: "description" });

  const {
    imageIndex,
    initialImages,
    handleFeaturedImageChange,
    handleRevertClick,
  } = useWidgetOperatorOverridesModal({
    form,
    operator,
  });

  if (!operator) {
    return null;
  }

  return (
    <Modal
      open={isOpen}
      placement={"centerPane"}
      onOpenChange={onClose}
      hideCloseTrigger
      noMaxH
      portal
    >
      {/* HEADER */}
      <Flex
        bg={"white"}
        p={"36px"}
        gap={"200"}
        borderBottomWidth={"1px"}
        borderBottomColor={"border.light"}
        borderBottomStyle={"solid"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Flex alignItems={"center"} gap={"150"}>
          <Icon Element={EditPencil} />
          <Heading size={"h5"} color={"text.dark"}>
            {`${t("widgets:DETAILS.MODALS.OPERATOR_OVERRIDES.TITLE")}: ${getMultilingualString(operator.name)}`}
          </Heading>
        </Flex>
        <Flex alignItems={"center"} gap={"150"}>
          <Button
            Icon={Xmark}
            visual={"unstyled"}
            onClick={onClose}
            color={"icon.light"}
            size="lg"
            disabled={form.formState.isSubmitting}
          />
        </Flex>
      </Flex>

      {/* CONTENT */}
      <Grid
        minW="min(864px, 100vw)"
        width="864px"
        maxH="max(500px, 50vh)"
        gridTemplateColumns={"repeat(2, 1fr)"}
        gap={0}
        bg="bg.wa.light"
        overflow="hidden"
      >
        <FormProvider {...form}>
          <form
            id={formId}
            onSubmit={onSubmit}
            style={{
              maxHeight: "max(500px, 50vh)",
              overflow: "auto",
            }}
          >
            <Flex
              flexDir="column"
              gap="300"
              p="36px"
              borderRightColor={"border.light"}
              borderRightWidth={"1px"}
              flex={1}
            >
              <Text visual="bodyMedium">
                {t("widgets:DETAILS.MODALS.OPERATOR_OVERRIDES.DESCRIPTION")}
              </Text>

              <ControlledTextArea
                name="shortDescription"
                label={t("widgets:DETAILS.FORM.LABELS.CARD_TEXT")}
                hint={t("widgets:DETAILS.FORM.LABELS.CARD_TEXT_HINT")}
                inputMode="text"
                maxLength={200}
                rows={3}
              />

              <ControlledTextArea
                name="description"
                label={t("widgets:DETAILS.FORM.LABELS.DESCRIPTION")}
                hint={t("widgets:DETAILS.FORM.LABELS.DESCRIPTION_HINT")}
                inputMode="text"
                rows={10}
              />

              {/* We only need to show the featured image section IF the operator has more than 1 image */}
              {initialImages.length > 1 && (
                <>
                  <Flex flexDir="column" gap="100">
                    <Text visual="bodySemiBold">
                      {t(
                        "widgets:DETAILS.MODALS.OPERATOR_OVERRIDES.FEATURED_IMAGE",
                      )}
                    </Text>
                    <Text visual="bodyRegular" color="text.mid">
                      {t(
                        "widgets:DETAILS.MODALS.OPERATOR_OVERRIDES.FEATURED_IMAGE_HINT",
                      )}
                    </Text>
                  </Flex>

                  <Carousel
                    borderRadius={"100"}
                    images={initialImages}
                    height="320px"
                    width="360px"
                    index={imageIndex}
                    onIndexChange={handleFeaturedImageChange}
                    render={(image: Partial<AssetImage>) => (
                      <CloudinaryImage
                        image={image}
                        imageStyleProps={{
                          h: "320px",
                          aspectRatio: "9 / 8",
                          width: "full",
                          maxW: "360px",
                        }}
                        resize={
                          isLandscape(image)
                            ? limitFill().height(320).gravity(autoGravity())
                            : limitFill().width(360).gravity(autoGravity())
                        }
                      />
                    )}
                  />
                </>
              )}
            </Flex>
          </form>
        </FormProvider>

        <Flex flexDir="column" p="36px" flex={1}>
          <OperatorWidgetCard
            operator={{
              ...operator,
              images: images as AssetImage[],
              shortDescription: { en: shortDescription },
            }}
            viewOnly={true}
            cardSurfaceProps={{
              bg: "white",
            }}
            orientation="portrait"
            organizationId={organizationId}
            widgetId={widget._id}
          />
        </Flex>
      </Grid>

      {/* FOOTER */}
      <Flex
        flexDir="row"
        justifyContent={"space-between"}
        alignItems={"center"}
        p={"36px"}
        gap="200"
        bg="bg.wa.light"
        borderTopColor={"border.light"}
        borderTopWidth={"1px"}
      >
        <Button
          visual="primary"
          mode="light"
          onClick={onClose}
          disabled={form.formState.isSubmitting}
        >
          {t("core:CANCEL")}
        </Button>
        <Flex
          flexDir="row"
          justifyContent={"space-between"}
          alignItems={"center"}
          gap="75"
        >
          <Button
            Icon={Undo}
            visual="primary"
            mode="light"
            onClick={handleRevertClick}
            disabled={form.formState.isSubmitting}
          >
            {t("core:RESET_ALL")}
          </Button>
          <Button
            visual="accent.blue"
            type="submit"
            form={formId}
            disabled={
              // The form is dirty when the shortDescription, description or array order is different
              _.isEqual(
                { images, shortDescription, description },
                form.formState.defaultValues,
              )
            }
            isLoading={form.formState.isSubmitting}
          >
            {t("core:SAVE_CHANGES")}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
